import { supabase } from '../lib/supabase';

// Notification types
export const NOTIFICATION_TYPES = {
  JOB_ASSIGNED: 'job_assigned',
  JOB_UPDATED: 'job_updated',
  REPORT_SUBMITTED: 'report_submitted',
  JOB_APPROVED: 'job_approved',
  JOB_REJECTED: 'job_rejected',
  JOB_NEEDS_REVISION: 'job_needs_revision',
  JOB_DISPUTED: 'job_disputed',
  JOB_PENDING: 'job_pending',
  JOB_PENDING_REPORT: 'job_pending_report',
  PAYMENT_RECORDED: 'payment_recorded',
  INVITE: 'invite',
  INVITE_ACCEPTED: 'invite_accepted',
  GENERAL: 'general',
  // Inspection notification types
  INSPECTION_ASSIGNED: 'inspection_assigned',
  INSPECTION_DUE_SOON: 'inspection_due_soon',
  INSPECTION_OVERDUE: 'inspection_overdue',
  INSPECTION_SUBMITTED: 'inspection_submitted',
  INSPECTION_APPROVED: 'inspection_approved',
  INSPECTION_REJECTED: 'inspection_rejected'
};

// Reference types
export const REFERENCE_TYPES = {
  JOB: 'job',
  JOB_INSTANCE: 'job_instance',
  INVITE: 'invite',
  USER: 'user',
  INSPECTION: 'inspection'
};

export const notificationService = {
  // Get notifications for current user
  async getUserNotifications(userId, options = {}) {
    const { limit = 20, offset = 0, unreadOnly = false, companyId = null } = options;

    let query = supabase
      ?.from('notifications')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false })
      ?.range(offset, offset + limit - 1);

    if (unreadOnly) {
      query = query?.eq('is_read', false);
    }

    if (companyId) {
      query = query?.eq('company_id', companyId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(notification => ({
      id: notification?.id,
      userId: notification?.user_id,
      companyId: notification?.company_id,
      type: notification?.type,
      title: notification?.title,
      message: notification?.message,
      referenceType: notification?.reference_type,
      referenceId: notification?.reference_id,
      isRead: notification?.is_read,
      createdAt: notification?.created_at
    }));
  },

  // Get unread notification count
  async getUnreadCount(userId, companyId = null) {
    let query = supabase
      ?.from('notifications')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('user_id', userId)
      ?.eq('is_read', false);

    if (companyId) {
      query = query?.eq('company_id', companyId);
    }

    const { count, error } = await query;

    if (error) throw error;

    return count || 0;
  },

  // Create a notification
  async createNotification(notificationData) {
    const { data, error } = await supabase
      ?.from('notifications')
      ?.insert({
        user_id: notificationData?.userId,
        company_id: notificationData?.companyId,
        type: notificationData?.type,
        title: notificationData?.title,
        message: notificationData?.message,
        reference_type: notificationData?.referenceType,
        reference_id: notificationData?.referenceId
      });

    if (error) throw error;

    return { success: true };
  },

  // Create multiple notifications (batch)
  async createNotifications(notificationsArray) {
    const insertData = notificationsArray?.map(n => ({
      user_id: n?.userId,
      company_id: n?.companyId,
      type: n?.type,
      title: n?.title,
      message: n?.message,
      reference_type: n?.referenceType,
      reference_id: n?.referenceId
    }));

    const { data, error } = await supabase
      ?.from('notifications')
      ?.insert(insertData)
      ?.select();

    if (error) throw error;

    return data;
  },

  // Mark a notification as read
  async markAsRead(notificationId) {
    const { error } = await supabase
      ?.from('notifications')
      ?.update({ is_read: true })
      ?.eq('id', notificationId);

    if (error) throw error;
  },

  // Mark all notifications as read for a user
  async markAllAsRead(userId, companyId = null) {
    let query = supabase
      ?.from('notifications')
      ?.update({ is_read: true })
      ?.eq('user_id', userId)
      ?.eq('is_read', false);

    if (companyId) {
      query = query?.eq('company_id', companyId);
    }

    const { error } = await query;

    if (error) throw error;
  },

  // Delete a notification
  async deleteNotification(notificationId) {
    const { error } = await supabase
      ?.from('notifications')
      ?.delete()
      ?.eq('id', notificationId);

    if (error) throw error;
  },

  // Delete old notifications (cleanup - older than 30 days)
  async deleteOldNotifications(userId, daysOld = 30) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);

    const { error } = await supabase
      ?.from('notifications')
      ?.delete()
      ?.eq('user_id', userId)
      ?.lt('created_at', cutoffDate.toISOString());

    if (error) throw error;
  },

  // Helper: Send job assigned notification to contractor
  async notifyJobAssigned(contractorId, companyId, jobInstance, jobName) {
    return this.createNotification({
      userId: contractorId,
      companyId: companyId,
      type: NOTIFICATION_TYPES.JOB_ASSIGNED,
      title: 'New Job Assigned',
      message: `You have been assigned to "${jobName}"`,
      referenceType: REFERENCE_TYPES.JOB_INSTANCE,
      referenceId: jobInstance?.id
    });
  },

  // Helper: Send report submitted notification to company managers
  async notifyReportSubmitted(managerIds, companyId, jobInstanceId, jobName, contractorName) {
    const notifications = managerIds?.map(managerId => ({
      userId: managerId,
      companyId: companyId,
      type: NOTIFICATION_TYPES.REPORT_SUBMITTED,
      title: 'Job Report Submitted',
      message: `${contractorName} has submitted a report for "${jobName}"`,
      referenceType: REFERENCE_TYPES.JOB_INSTANCE,
      referenceId: jobInstanceId
    }));

    return this.createNotifications(notifications);
  },

  // Helper: Send job approved notification
  async notifyJobApproved(contractorId, companyId, jobInstanceId, jobName) {
    return this.createNotification({
      userId: contractorId,
      companyId: companyId,
      type: NOTIFICATION_TYPES.JOB_APPROVED,
      title: 'Job Approved',
      message: `Your work on "${jobName}" has been approved`,
      referenceType: REFERENCE_TYPES.JOB_INSTANCE,
      referenceId: jobInstanceId
    });
  },

  // Helper: Send job needs revision notification
  async notifyJobNeedsRevision(contractorId, companyId, jobInstanceId, jobName, comment) {
    return this.createNotification({
      userId: contractorId,
      companyId: companyId,
      type: NOTIFICATION_TYPES.JOB_NEEDS_REVISION,
      title: 'Revision Requested',
      message: comment ? `Revision needed for "${jobName}": ${comment}` : `Revision needed for "${jobName}"`,
      referenceType: REFERENCE_TYPES.JOB_INSTANCE,
      referenceId: jobInstanceId
    });
  },

  // Helper: Send payment recorded notification to contractor
  async notifyPaymentRecorded(contractorId, companyId, jobInstanceId, jobName, amount, transactionRef) {
    // Format amount as currency
    const formattedAmount = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);

    return this.createNotification({
      userId: contractorId,
      companyId: companyId,
      type: NOTIFICATION_TYPES.PAYMENT_RECORDED,
      title: 'Payment Recorded',
      message: `Payment of ${formattedAmount} recorded for "${jobName}". Ref: ${transactionRef}`,
      referenceType: REFERENCE_TYPES.JOB_INSTANCE,
      referenceId: jobInstanceId
    });
  },

  // Helper: Send job pending notification (scheduled time passed, not clocked in)
  async notifyJobPending(userId, companyId, jobInstanceId, jobName, isContractor = true) {
    return this.createNotification({
      userId: userId,
      companyId: companyId,
      type: NOTIFICATION_TYPES.JOB_PENDING,
      title: 'Job Overdue',
      message: isContractor
        ? `"${jobName}" is overdue - scheduled time has passed. Please clock in or contact your supervisor.`
        : `"${jobName}" is overdue - scheduled time has passed and work hasn't started.`,
      referenceType: REFERENCE_TYPES.JOB_INSTANCE,
      referenceId: jobInstanceId
    });
  },

  // Helper: Send job pending report notification (clocked in, report overdue)
  async notifyJobPendingReport(userId, companyId, jobInstanceId, jobName, isContractor = true) {
    return this.createNotification({
      userId: userId,
      companyId: companyId,
      type: NOTIFICATION_TYPES.JOB_PENDING_REPORT,
      title: 'Report Overdue',
      message: isContractor
        ? `Report for "${jobName}" is overdue. Please submit your work report.`
        : `Report for "${jobName}" is overdue - work started but no report submitted.`,
      referenceType: REFERENCE_TYPES.JOB_INSTANCE,
      referenceId: jobInstanceId
    });
  },

  // =========================================================================
  // INSPECTION NOTIFICATION HELPERS
  // =========================================================================

  // Helper: Send inspection assigned notification to assignee
  async notifyInspectionAssigned(assigneeId, companyId, inspectionId, templateName, siteName) {
    return this.createNotification({
      userId: assigneeId,
      companyId: companyId,
      type: NOTIFICATION_TYPES.INSPECTION_ASSIGNED,
      title: 'New Inspection Assigned',
      message: `You have been assigned "${templateName}" inspection at ${siteName}`,
      referenceType: REFERENCE_TYPES.INSPECTION,
      referenceId: inspectionId
    });
  },

  // Helper: Send inspection due soon notification
  async notifyInspectionDueSoon(userId, companyId, inspectionId, templateName, siteName, dueDate) {
    const formattedDate = new Date(dueDate).toLocaleDateString('en-AU', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
    return this.createNotification({
      userId: userId,
      companyId: companyId,
      type: NOTIFICATION_TYPES.INSPECTION_DUE_SOON,
      title: 'Inspection Due Soon',
      message: `"${templateName}" at ${siteName} is due ${formattedDate}`,
      referenceType: REFERENCE_TYPES.INSPECTION,
      referenceId: inspectionId
    });
  },

  // Helper: Send inspection overdue notification
  async notifyInspectionOverdue(userId, companyId, inspectionId, templateName, siteName) {
    return this.createNotification({
      userId: userId,
      companyId: companyId,
      type: NOTIFICATION_TYPES.INSPECTION_OVERDUE,
      title: 'Inspection Overdue',
      message: `"${templateName}" at ${siteName} is overdue. Please complete it as soon as possible.`,
      referenceType: REFERENCE_TYPES.INSPECTION,
      referenceId: inspectionId
    });
  },

  // Helper: Send inspection submitted notification to managers
  async notifyInspectionSubmitted(managerIds, companyId, inspectionId, templateName, siteName, inspectorName, score = null) {
    const scoreText = score !== null ? ` (Score: ${score}%)` : '';
    const notifications = managerIds?.map(managerId => ({
      userId: managerId,
      companyId: companyId,
      type: NOTIFICATION_TYPES.INSPECTION_SUBMITTED,
      title: 'Inspection Submitted',
      message: `${inspectorName} submitted "${templateName}" at ${siteName}${scoreText}`,
      referenceType: REFERENCE_TYPES.INSPECTION,
      referenceId: inspectionId
    }));

    return this.createNotifications(notifications);
  },

  // Helper: Send inspection approved notification
  async notifyInspectionApproved(inspectorId, companyId, inspectionId, templateName, siteName) {
    return this.createNotification({
      userId: inspectorId,
      companyId: companyId,
      type: NOTIFICATION_TYPES.INSPECTION_APPROVED,
      title: 'Inspection Approved',
      message: `Your "${templateName}" inspection at ${siteName} has been approved`,
      referenceType: REFERENCE_TYPES.INSPECTION,
      referenceId: inspectionId
    });
  },

  // Helper: Send inspection rejected notification
  async notifyInspectionRejected(inspectorId, companyId, inspectionId, templateName, siteName, reason) {
    return this.createNotification({
      userId: inspectorId,
      companyId: companyId,
      type: NOTIFICATION_TYPES.INSPECTION_REJECTED,
      title: 'Inspection Rejected',
      message: reason
        ? `"${templateName}" at ${siteName} was rejected: ${reason}`
        : `"${templateName}" at ${siteName} was rejected. Please redo the inspection.`,
      referenceType: REFERENCE_TYPES.INSPECTION,
      referenceId: inspectionId
    });
  },

  // Subscribe to real-time notifications (returns unsubscribe function)
  subscribeToNotifications(userId, onNotification) {
    const subscription = supabase
      ?.channel(`notifications:${userId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          const notification = payload?.new;
          onNotification({
            id: notification?.id,
            userId: notification?.user_id,
            companyId: notification?.company_id,
            type: notification?.type,
            title: notification?.title,
            message: notification?.message,
            referenceType: notification?.reference_type,
            referenceId: notification?.reference_id,
            isRead: notification?.is_read,
            createdAt: notification?.created_at
          });
        }
      )
      ?.subscribe();

    // Return unsubscribe function
    return () => {
      supabase?.removeChannel(subscription);
    };
  }
};
